import {
  ApiBlueprint,
  type FrontendFeature,
} from '@backstage/frontend-plugin-api';
import {
  convertLegacyPageExtension,
  convertLegacyPlugin,
} from '@backstage/core-compat-api';
import {
  attachComponentData,
  createApiFactory,
  discoveryApiRef,
  fetchApiRef,
  getComponentData,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';
import type { Entity } from '@backstage/catalog-model';
import {
  convertLegacyEntityCardExtension,
  convertLegacyEntityContentExtension,
} from '@backstage/plugin-catalog-react/alpha';
import appVisualizerPlugin from '@backstage/plugin-app-visualizer';
import catalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import catalogImportPlugin from '@backstage/plugin-catalog-import/alpha';
import catalogUnprocessedEntitiesPlugin from '@backstage/plugin-catalog-unprocessed-entities/alpha';
import orgPlugin from '@backstage/plugin-org/alpha';
import {
  MovedState,
  techRadarApiRef,
  techRadarPlugin,
  type TechRadarApi,
  type TechRadarLoaderResponse,
} from '@backstage/plugin-tech-radar';
import {
  techDocsExpandableNavigationAddonModule,
  techDocsLightBoxAddonModule,
  techDocsReportIssueAddonModule,
  techDocsTextSizeAddonModule,
} from '@backstage/plugin-techdocs-module-addons-contrib/alpha';
import {
  EntityAzureGitTagsContent,
  EntityAzurePipelinesContent,
  EntityAzurePullRequestsContent,
  EntityAzureReadmeCard,
  azureDevOpsPlugin,
  isAzureDevOpsAvailable,
  isAzurePipelinesAvailable,
} from '@backstage/plugin-azure-devops';
import {
  githubActionsPlugin,
} from '@backstage/plugin-github-actions';
import {
  EntityJenkinsContent,
  EntityLatestJenkinsRunCard,
  isJenkinsAvailable,
  jenkinsPlugin,
} from '@backstage/plugin-jenkins';
import {
  EntityLastLighthouseAuditCard,
  EntityLighthouseContent,
  isLighthouseAvailable,
  lighthousePlugin,
} from '@backstage/plugin-lighthouse';
import {
  EntityTechInsightsScorecardCard,
  EntityTechInsightsScorecardContent,
  techInsightsPlugin,
} from '@backstage/plugin-tech-insights';
import {
  EntityLinguistCard,
  isLinguistAvailable,
  linguistPlugin,
} from '@backstage/plugin-linguist';
import { badgesPlugin } from '@backstage/plugin-badges';
import { PlaylistIndexPage, playlistPlugin } from '@backstage/plugin-playlist';
import {
  EntityPagerDutyCard,
  isPagerDutyAvailable,
  pagerDutyPlugin,
} from '@backstage/plugin-pagerduty';
import {
  EntitySentryContent,
  isSentryAvailable,
  sentryPlugin,
} from '@backstage/plugin-sentry';
import { EntityTodoContent, todoPlugin } from '@backstage/plugin-todo';
import type { ComponentType, ReactNode } from 'react';
import {
  EntityC4ciGithubActionsCard,
  EntityC4ciGithubActionsContent,
  isC4ciGithubActionsAvailable,
} from './factoryPortal/FactoryCicdContent';

type RuntimeRadarResponse = {
  quadrants: Array<{ id: string; name: string }>;
  rings: Array<{
    id: string;
    name: string;
    color: string;
    description: string;
  }>;
  entries: Array<{
    key: string;
    id: string;
    title: string;
    quadrant: string;
    description: string;
    links?: Array<{ url: string; title: string }>;
    timeline: Array<{
      date: string;
      ringId: string;
      moved: 'up' | 'down' | 'no-change';
      description?: string;
    }>;
  }>;
};

type RuntimeRadarEntry = RuntimeRadarResponse['entries'][number];

class C4ciTechRadarApi implements TechRadarApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async load(): Promise<TechRadarLoaderResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('c4ci-factory-runtime');
    const response = await this.fetchApi.fetch(`${baseUrl}/v1/tech-radar`);
    if (!response.ok) {
      throw new Error(
        `C4CI tech radar backend returned ${response.status}`,
      );
    }

    const summary = (await response.json()) as RuntimeRadarResponse;

    const toMovedState = (moved: RuntimeRadarEntry['timeline'][number]['moved']) => {
      if (moved === 'up') {
        return MovedState.Up;
      }
      if (moved === 'down') {
        return MovedState.Down;
      }
      return MovedState.NoChange;
    };

    return {
      quadrants: summary.quadrants,
      rings: summary.rings,
      entries: summary.entries.map(entry => ({
        ...entry,
        timeline: entry.timeline.map(item => ({
          ...item,
          date: new Date(item.date),
          moved: toMovedState(item.moved),
        })),
      })),
    };
  }
}

const c4ciTechRadarApi = ApiBlueprint.make({
  name: 'c4ci-tech-radar-api',
  params: defineParams => defineParams(
    createApiFactory({
      api: techRadarApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new C4ciTechRadarApi(discoveryApi, fetchApi),
    }),
  ),
});

function hasGithubSourceLocation(entity: Entity): boolean {
  const sourceLocation =
    entity.metadata.annotations?.['backstage.io/source-location'];

  return (
    entity.kind.toLocaleLowerCase('en-US') === 'component' &&
    typeof sourceLocation === 'string' &&
    sourceLocation.startsWith('url:https://github.com/')
  );
}

function isComponentEntity(entity: Entity): boolean {
  return entity.kind.toLocaleLowerCase('en-US') === 'component';
}

function forwardCoreComponentData(
  sourceNode: ReactNode,
  targetComponent: ComponentType<{}>,
  keys: string[],
): void {
  for (const key of keys) {
    const value = getComponentData(sourceNode, key);
    if (value !== undefined) {
      attachComponentData(targetComponent, key, value);
    }
  }
}

const EntityLatestJenkinsDevelopmentRunCard = () => (
  <EntityLatestJenkinsRunCard branch="development" />
);

const EntityC4ciTechInsightsScorecardContent = () => (
  <EntityTechInsightsScorecardContent
    description="C4CI product readiness checks from catalog and runtime evidence."
    title="C4CI Scorecards"
  />
);

const EntityC4ciTechInsightsScorecardCard = () => (
  <EntityTechInsightsScorecardCard
    description="C4CI product readiness checks from catalog and runtime evidence."
    title="C4CI Scorecards"
  />
);

attachComponentData(
  EntityC4ciTechInsightsScorecardContent,
  'core.extensionName',
  'EntityC4ciTechInsightsScorecardContent',
);
attachComponentData(
  EntityC4ciTechInsightsScorecardCard,
  'core.extensionName',
  'EntityC4ciTechInsightsScorecardCard',
);
forwardCoreComponentData(
  <EntityTechInsightsScorecardContent title="C4CI Scorecards" />,
  EntityC4ciTechInsightsScorecardContent,
  ['core.plugin', 'core.mountPoint'],
);
forwardCoreComponentData(
  <EntityTechInsightsScorecardCard title="C4CI Scorecards" />,
  EntityC4ciTechInsightsScorecardCard,
  ['core.plugin'],
);

const LIVE_INTEGRATION_ANNOTATIONS = {
  jenkins: 'c4ci.io/live-integration.jenkins',
  'azure-devops': 'c4ci.io/live-integration.azure-devops',
  pagerduty: 'c4ci.io/live-integration.pagerduty',
  sentry: 'c4ci.io/live-integration.sentry',
  lighthouse: 'c4ci.io/live-integration.lighthouse',
} as const;

function readAnnotation(entity: Entity, key: string): string {
  const value = entity.metadata.annotations?.[key];
  return typeof value === 'string' ? value : '';
}

function hasLiveIntegrationGate(
  entity: Entity,
  integration: keyof typeof LIVE_INTEGRATION_ANNOTATIONS,
): boolean {
  return (
    readAnnotation(entity, LIVE_INTEGRATION_ANNOTATIONS[integration])
      .toLowerCase()
      .trim() === 'true'
  );
}

function isC4ciJenkinsAvailable(entity: Entity): boolean {
  return isJenkinsAvailable(entity) && hasLiveIntegrationGate(entity, 'jenkins');
}

function isC4ciAzureDevOpsAvailable(entity: Entity): boolean {
  return (
    isAzureDevOpsAvailable(entity) &&
    hasLiveIntegrationGate(entity, 'azure-devops')
  );
}

function isC4ciAzurePipelinesAvailable(entity: Entity): boolean {
  return (
    isAzurePipelinesAvailable(entity) &&
    hasLiveIntegrationGate(entity, 'azure-devops')
  );
}

function isC4ciPagerDutyAvailable(entity: Entity): boolean {
  return (
    isPagerDutyAvailable(entity) && hasLiveIntegrationGate(entity, 'pagerduty')
  );
}

function isC4ciSentryAvailable(entity: Entity): boolean {
  return isSentryAvailable(entity) && hasLiveIntegrationGate(entity, 'sentry');
}

function isC4ciLighthouseAvailable(entity: Entity): boolean {
  return (
    isLighthouseAvailable(entity) && hasLiveIntegrationGate(entity, 'lighthouse')
  );
}

function attachLegacyExtensionMetadata<P>(
  target: ComponentType<P>,
  sourceElement: ReactNode,
  extensionName: string,
) {
  attachComponentData(target, 'core.extensionName', extensionName);

  const plugin = getComponentData(sourceElement, 'core.plugin');
  if (plugin) {
    attachComponentData(target, 'core.plugin', plugin);
  }
}

attachComponentData(
  EntityC4ciGithubActionsContent,
  'core.extensionName',
  'EntityC4ciGithubActionsContent',
);
attachComponentData(
  EntityC4ciGithubActionsContent,
  'core.plugin',
  githubActionsPlugin,
);
attachComponentData(
  EntityC4ciGithubActionsCard,
  'core.extensionName',
  'EntityC4ciGithubActionsCard',
);
attachComponentData(
  EntityC4ciGithubActionsCard,
  'core.plugin',
  githubActionsPlugin,
);
attachLegacyExtensionMetadata(
  EntityLatestJenkinsDevelopmentRunCard,
  <EntityLatestJenkinsRunCard branch="development" />,
  'EntityLatestJenkinsDevelopmentRunCard',
);

export const factoryPortalBackstageFeatures: FrontendFeature[] = [
  appVisualizerPlugin,
  catalogGraphPlugin,
  catalogImportPlugin,
  catalogUnprocessedEntitiesPlugin,
  orgPlugin,
  convertLegacyPlugin(techRadarPlugin, {
    extensions: [c4ciTechRadarApi],
  }),
  techDocsExpandableNavigationAddonModule,
  techDocsLightBoxAddonModule,
  techDocsReportIssueAddonModule,
  techDocsTextSizeAddonModule,
  convertLegacyPlugin(githubActionsPlugin, {
    extensions: [
      convertLegacyEntityContentExtension(EntityC4ciGithubActionsContent, {
        filter: isC4ciGithubActionsAvailable,
        path: '/ci-cd',
        title: 'CI/CD',
      }),
      convertLegacyEntityCardExtension(EntityC4ciGithubActionsCard, {
        filter: isC4ciGithubActionsAvailable,
      }),
    ],
  }),
  convertLegacyPlugin(jenkinsPlugin, {
    extensions: [
      convertLegacyEntityContentExtension(EntityJenkinsContent, {
        filter: isC4ciJenkinsAvailable,
        path: '/jenkins',
        title: 'Jenkins',
      }),
      convertLegacyEntityCardExtension(EntityLatestJenkinsDevelopmentRunCard, {
        filter: isC4ciJenkinsAvailable,
      }),
    ],
  }),
  convertLegacyPlugin(azureDevOpsPlugin, {
    extensions: [
      convertLegacyEntityContentExtension(EntityAzurePipelinesContent, {
        filter: isC4ciAzurePipelinesAvailable,
        path: '/azure-pipelines',
        title: 'Azure Pipelines',
      }),
      convertLegacyEntityContentExtension(EntityAzurePullRequestsContent, {
        filter: isC4ciAzureDevOpsAvailable,
        path: '/azure-pull-requests',
        title: 'Azure Pull Requests',
      }),
      convertLegacyEntityContentExtension(EntityAzureGitTagsContent, {
        filter: isC4ciAzureDevOpsAvailable,
        path: '/azure-git-tags',
        title: 'Azure Git Tags',
      }),
      convertLegacyEntityCardExtension(EntityAzureReadmeCard, {
        filter: isC4ciAzureDevOpsAvailable,
      }),
    ],
  }),
  convertLegacyPlugin(todoPlugin, {
    extensions: [
      convertLegacyEntityContentExtension(EntityTodoContent, {
        filter: hasGithubSourceLocation,
        path: '/todos',
        title: 'TODOs',
      }),
    ],
  }),
  convertLegacyPlugin(pagerDutyPlugin, {
    extensions: [
      convertLegacyEntityCardExtension(EntityPagerDutyCard, {
        filter: isC4ciPagerDutyAvailable,
      }),
    ],
  }),
  convertLegacyPlugin(sentryPlugin, {
    extensions: [
      convertLegacyEntityContentExtension(EntitySentryContent, {
        filter: isC4ciSentryAvailable,
        path: '/sentry',
        title: 'Sentry',
      }),
    ],
  }),
  convertLegacyPlugin(lighthousePlugin, {
    extensions: [
      convertLegacyEntityContentExtension(EntityLighthouseContent, {
        filter: isC4ciLighthouseAvailable,
        path: '/lighthouse',
        title: 'Lighthouse',
      }),
      convertLegacyEntityCardExtension(EntityLastLighthouseAuditCard, {
        filter: isC4ciLighthouseAvailable,
      }),
    ],
  }),
  convertLegacyPlugin(techInsightsPlugin, {
    extensions: [
      convertLegacyEntityContentExtension(EntityC4ciTechInsightsScorecardContent, {
        filter: isComponentEntity,
        path: '/scorecards',
        title: 'Scorecards',
      }),
      convertLegacyEntityCardExtension(EntityC4ciTechInsightsScorecardCard, {
        filter: isComponentEntity,
      }),
    ],
  }),
  convertLegacyPlugin(linguistPlugin, {
    extensions: [
      convertLegacyEntityCardExtension(EntityLinguistCard, {
        filter: isLinguistAvailable,
      }),
    ],
  }),
  convertLegacyPlugin(badgesPlugin, {
    extensions: [],
  }),
  convertLegacyPlugin(playlistPlugin, {
    extensions: [
      convertLegacyPageExtension(PlaylistIndexPage, {
        path: '/playlists',
      }),
    ],
  }),
];
